/**
 * Finance — Phase 03 redesign
 * Overview dashboard: KPI strip, revenue chart, recent invoices, expense management
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Icons,
  KPI, KPIStrip,
  Drawer, DrawerBody, DrawerSection, DrawerFooter, KVList,
  EmptyState, QueryErrorBanner,
  CurrencyInput,
} from '../shared/ui';
import api from '../utils/api';
import FinanceBillingReport from './FinanceBillingReport';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler,
);

// ── helpers ───────────────────────────────────────────────────────────────────
const VND = (n, lang = 'vi') =>
  new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'vi-VN', {
    style: 'currency', currency: 'VND',
  }).format(n ?? 0);

const intlLocale = (lang) => (lang === 'en' ? 'en-US' : 'vi-VN');

const nowYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const lastNMonths = (n) => {
  const result = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return result;
};

// Expense category keys — values come from i18n at render time.
// Aligned with both the backend's EXPENSE_CATEGORIES dict and each building's
// services_config (electric/water/internet/elevator/cleaning/washing/parking_*).
// `electricity` and `repair` are kept as legacy aliases so existing stored
// expenses don't lose their dropdown label.
const EXPENSE_CATEGORY_KEYS = [
  // Operational — services that also generate matching income (P&L pairing)
  'electricity', 'water', 'internet', 'elevator', 'cleaning', 'washing',
  'parking_motorbike', 'parking_car',
  // Overhead — no matching income service
  'repair', 'maintenance', 'management', 'insurance', 'security', 'tax', 'legal',
  // Agent/broker
  'commission',
  'other',
];

function useStatusMeta() {
  const { t } = useTranslation('finance');
  return {
    confirmed: { label: t('status.confirmed'), cls: 'text-green-700 bg-green-50  ring-green-200' },
    partial:   { label: t('status.partial'),   cls: 'text-amber-700 bg-amber-50  ring-amber-200' },
    pending:   { label: t('status.pending'),   cls: 'text-teal-700  bg-teal-50   ring-teal-200'  },
    rejected:  { label: t('status.rejected'),  cls: 'text-red-700   bg-red-50    ring-red-200'   },
  };
}

function StatusPill({ status }) {
  const meta = useStatusMeta();
  const m = meta[status] ?? { label: status, cls: 'text-stone-600 bg-stone-100 ring-stone-200' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${m.cls}`}>
      {m.label}
    </span>
  );
}

// ── chart defaults ────────────────────────────────────────────────────────────
const chartFont = { family: 'Be Vietnam Pro, sans-serif', size: 11 };
const TEAL = '#0d9488';
const TEAL_LIGHT = '#ccfbf1';
const STONE = '#78716c';

// ── Expense drawer ─────────────────────────────────────────────────────────────
const emptyForm = () => ({
  title: '',
  amount: '',
  category: '',
  expense_date: new Date().toISOString().split('T')[0],
  billing_month: nowYM(),
  building_id: '',
  notes: '',
});

function ExpenseDrawer({ open, onClose, editing, buildings, onSuccess }) {
  const qc = useQueryClient();
  const { t } = useTranslation(['finance', 'common']);
  const { data: categories = [] } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get('/api/expenses/categories/standard').then(r => Array.isArray(r.data) ? r.data : []),
    staleTime: 5 * 60 * 1000,
  });
  const [form, setForm] = useState(editing ? {
    title: editing.title ?? '',
    amount: String(editing.amount ?? ''),
    category: editing.category ?? '',
    expense_date: editing.expense_date ?? new Date().toISOString().split('T')[0],
    billing_month: editing.billing_month ?? nowYM(),
    building_id: String(editing.building_id ?? ''),
    notes: editing.notes ?? '',
  } : emptyForm());

  React.useEffect(() => {
    setForm(editing ? {
      title: editing.title ?? '',
      amount: String(editing.amount ?? ''),
      category: editing.category ?? '',
      expense_date: editing.expense_date ?? new Date().toISOString().split('T')[0],
      billing_month: editing.billing_month ?? nowYM(),
      building_id: String(editing.building_id ?? ''),
      notes: editing.notes ?? '',
    } : emptyForm());
  }, [editing, open]);

  const save = useMutation({
    mutationFn: (data) => editing
      ? api.put(`/api/expenses/${editing.id}`, data).then(r => r.data)
      : api.post('/api/expenses', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense-stats'] });
      toast.success(editing ? 'Đã cập nhật chi phí' : 'Đã thêm chi phí');
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || 'Lưu thất bại');
    },
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-900 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';

  const handleSubmit = (e) => {
    e.preventDefault();
    save.mutate({
      ...form,
      amount: parseFloat(form.amount),
      building_id: form.building_id ? parseInt(form.building_id) : null,
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? t('finance:expense.drawer.titleEdit') : t('finance:expense.drawer.titleNew')}
      width={380}
    >
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <DrawerBody>
          <DrawerSection>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  {t('finance:expense.drawer.label.description')} *
                </label>
                <input
                  required
                  value={form.title}
                  onChange={set('title')}
                  placeholder={t('finance:expense.drawer.label.descriptionPlaceholder')}
                  className={fieldCls}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  {t('finance:expense.drawer.label.amount')} *
                </label>
                <CurrencyInput
                  required
                  value={form.amount}
                  onChange={set('amount')}
                  placeholder="0"
                  className={fieldCls}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  {t('finance:expense.drawer.label.category')}
                </label>
                <select value={form.category} onChange={set('category')} className={fieldCls}>
                  <option value="">{t('finance:expense.drawer.label.categoryPlaceholder')}</option>
                  {categories.filter(c => c.is_active !== false).map(c => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">
                    {t('finance:expense.drawer.label.expenseDate')}
                  </label>
                  <input
                    type="date"
                    value={form.expense_date}
                    onChange={set('expense_date')}
                    className={fieldCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">
                    {t('finance:expense.drawer.label.billingMonth')}
                  </label>
                  <input
                    type="month"
                    value={form.billing_month}
                    onChange={set('billing_month')}
                    className={fieldCls}
                  />
                </div>
              </div>
              {buildings.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">
                    {t('finance:expense.drawer.label.building')}
                  </label>
                  <select value={form.building_id} onChange={set('building_id')} className={fieldCls}>
                    <option value="">{t('finance:expense.drawer.label.buildingAll')}</option>
                    {buildings.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  {t('finance:expense.drawer.label.notes')}
                </label>
                <textarea
                  value={form.notes}
                  onChange={set('notes')}
                  rows={3}
                  placeholder={t('finance:expense.drawer.label.notesPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                />
              </div>
            </div>
          </DrawerSection>
        </DrawerBody>
        <DrawerFooter>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors"
          >
            {t('common:action.cancel')}
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {save.isPending
              ? t('common:status.saving')
              : editing
                ? t('finance:expense.drawer.submit.update')
                : t('finance:expense.drawer.submit.create')}
          </button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}

// ── Expense category drawer ───────────────────────────────────────────────────
const emptyCatForm = () => ({ key: '', label: '', type: 'overhead', paired_service: '', sort_order: 0, is_active: true });

function ExpenseCategoryDrawer({ open, onClose, editing }) {
  const { t } = useTranslation(['finance', 'common']);
  const qc = useQueryClient();
  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';
  const [form, setForm] = useState(emptyCatForm);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  useEffect(() => {
    if (open) setForm(editing ? {
      key: editing.key ?? '',
      label: editing.label ?? '',
      type: editing.type ?? 'overhead',
      paired_service: editing.paired_service ?? '',
      sort_order: editing.sort_order ?? 0,
      is_active: editing.is_active ?? true,
    } : emptyCatForm());
  }, [open, editing]);

  const save = useMutation({
    mutationFn: (data) => editing
      ? api.put(`/api/expenses/categories/${editing.key}`, data).then(r => r.data)
      : api.post('/api/expenses/categories', data).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expense-categories'] }); onClose(); },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    save.mutate({
      key: form.key.trim().toLowerCase().replace(/\s+/g, '_'),
      label: form.label.trim(),
      type: form.type,
      paired_service: form.paired_service.trim() || null,
      sort_order: parseInt(form.sort_order) || 0,
      is_active: form.is_active,
    });
  };

  return (
    <Drawer open={open} onClose={onClose}
      title={editing ? t('finance:expenseCategory.drawer.titleEdit') : t('finance:expenseCategory.drawer.titleNew')}
      width={360}>
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <DrawerBody>
          <DrawerSection>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseCategory.drawer.label')} *</label>
                <input required value={form.label} onChange={set('label')}
                  placeholder={t('finance:expenseCategory.drawer.labelPlaceholder')} className={fieldCls} />
              </div>
              {!editing && (
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseCategory.drawer.key')} *</label>
                  <input required value={form.key} onChange={set('key')}
                    placeholder={t('finance:expenseCategory.drawer.keyPlaceholder')} className={fieldCls} />
                  <p className="text-[11px] text-stone-400 mt-1">{t('finance:expenseCategory.drawer.keyHint')}</p>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseCategory.drawer.type')}</label>
                <select value={form.type} onChange={set('type')} className={fieldCls}>
                  <option value="overhead">{t('finance:expenseCategory.typeOverhead')}</option>
                  <option value="operational">{t('finance:expenseCategory.typeOperational')}</option>
                </select>
              </div>
              {form.type === 'operational' && (
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseCategory.drawer.pairedService')}</label>
                  <input value={form.paired_service} onChange={set('paired_service')}
                    placeholder="e.g. internet" className={fieldCls} />
                  <p className="text-[11px] text-stone-400 mt-1">{t('finance:expenseCategory.drawer.pairedServiceHint')}</p>
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={set('is_active')}
                  className="w-4 h-4 rounded border-stone-300 text-teal-600" />
                <span className="text-[13px] text-stone-700">{t('finance:expenseTemplate.drawer.active')}</span>
              </label>
            </div>
          </DrawerSection>
        </DrawerBody>
        <DrawerFooter>
          <button type="button" onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50">
            {t('common:action.cancel')}
          </button>
          <button type="submit" disabled={save.isPending}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 disabled:opacity-50">
            {save.isPending ? t('common:status.saving') : editing ? t('finance:expense.drawer.submit.update') : t('finance:expense.drawer.submit.create')}
          </button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}

// ── Expense template drawer ───────────────────────────────────────────────────
const TEMPLATE_TYPES = ['fixed', 'per_unit', 'percentage'];

const emptyTmplForm = () => ({
  name: '', category: '', template_type: 'fixed',
  base_amount: '', unit_rate: '', unit_name: '', percentage_rate: '',
  building_id: '', description: '', is_active: true,
});

function ExpenseTemplateDrawer({ open, onClose, editing, buildings }) {
  const { t } = useTranslation(['finance', 'common']);
  const qc = useQueryClient();
  const { data: categories = [] } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get('/api/expenses/categories/standard').then(r => Array.isArray(r.data) ? r.data : []),
    staleTime: 5 * 60 * 1000,
  });
  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';

  const [form, setForm] = useState(emptyTmplForm);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        name: editing.name ?? '',
        category: editing.category ?? '',
        template_type: editing.template_type ?? 'fixed',
        base_amount: editing.base_amount != null ? String(editing.base_amount) : '',
        unit_rate: editing.unit_rate != null ? String(editing.unit_rate) : '',
        unit_name: editing.unit_name ?? '',
        percentage_rate: editing.percentage_rate != null ? String(editing.percentage_rate) : '',
        building_id: editing.building_id != null ? String(editing.building_id) : '',
        description: editing.description ?? '',
        is_active: editing.is_active ?? true,
      } : emptyTmplForm());
    }
  }, [open, editing]);

  const save = useMutation({
    mutationFn: (data) => editing
      ? api.put(`/api/expense-templates/${editing.id}`, data).then(r => r.data)
      : api.post('/api/expense-templates', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expense-templates'] });
      onClose();
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      name: form.name,
      category: form.category,
      template_type: form.template_type,
      base_amount: form.base_amount ? parseFloat(form.base_amount) : null,
      unit_rate: form.unit_rate ? parseFloat(form.unit_rate) : null,
      unit_name: form.unit_name || null,
      percentage_rate: form.percentage_rate ? parseFloat(form.percentage_rate) : null,
      building_id: form.building_id ? parseInt(form.building_id) : null,
      description: form.description || null,
      is_active: form.is_active,
    };
    save.mutate(data);
  };

  return (
    <Drawer open={open} onClose={onClose} title={editing ? t('finance:expenseTemplate.drawer.titleEdit') : t('finance:expenseTemplate.drawer.titleNew')} width={380}>
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <DrawerBody>
          <DrawerSection>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseTemplate.drawer.name')} *</label>
                <input required value={form.name} onChange={set('name')}
                  placeholder={t('finance:expenseTemplate.drawer.namePlaceholder')} className={fieldCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseTemplate.drawer.category')}</label>
                  <select value={form.category} onChange={set('category')} className={fieldCls}>
                    <option value="">—</option>
                    {categories.filter(c => c.is_active !== false).map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseTemplate.drawer.type')}</label>
                  <select value={form.template_type} onChange={set('template_type')} className={fieldCls}>
                    {TEMPLATE_TYPES.map(tp => (
                      <option key={tp} value={tp}>{t(`finance:expenseTemplate.type.${tp}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
              {form.template_type === 'fixed' && (
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseTemplate.drawer.baseAmount')}</label>
                  <CurrencyInput value={form.base_amount} onChange={set('base_amount')} className={fieldCls} />
                </div>
              )}
              {form.template_type === 'per_unit' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseTemplate.drawer.unitRate')}</label>
                    <CurrencyInput value={form.unit_rate} onChange={set('unit_rate')} className={fieldCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseTemplate.drawer.unitName')}</label>
                    <input value={form.unit_name} onChange={set('unit_name')}
                      placeholder={t('finance:expenseTemplate.drawer.unitNamePlaceholder')} className={fieldCls} />
                  </div>
                </div>
              )}
              {form.template_type === 'percentage' && (
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseTemplate.drawer.percentageRate')} (%)</label>
                  <input type="number" step="0.01" min="0" max="100"
                    value={form.percentage_rate} onChange={set('percentage_rate')} className={fieldCls} />
                </div>
              )}
              {buildings.length > 1 && (
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expense.drawer.label.building')}</label>
                  <select value={form.building_id} onChange={set('building_id')} className={fieldCls}>
                    <option value="">{t('finance:expense.drawer.label.buildingAll')}</option>
                    {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">{t('finance:expenseTemplate.drawer.description')}</label>
                <textarea value={form.description} onChange={set('description')} rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active} onChange={set('is_active')}
                  className="w-4 h-4 rounded border-stone-300 text-teal-600" />
                <span className="text-[13px] text-stone-700">{t('finance:expenseTemplate.drawer.active')}</span>
              </label>
            </div>
          </DrawerSection>
        </DrawerBody>
        <DrawerFooter>
          <button type="button" onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors">
            {t('common:action.cancel')}
          </button>
          <button type="submit" disabled={save.isPending}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50">
            {save.isPending ? t('common:status.saving') : editing ? t('finance:expense.drawer.submit.update') : t('finance:expense.drawer.submit.create')}
          </button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────
export default function Finance() {
  const qc = useQueryClient();
  const { t, i18n } = useTranslation(['finance', 'common']);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const [activeTab, setActiveTab] = useState('overview');
  const [expenseDrawer, setExpenseDrawer] = useState({ open: false, editing: null });
  const [tmplDrawer, setTmplDrawer] = useState({ open: false, editing: null });
  const [catDrawer, setCatDrawer] = useState({ open: false, editing: null });
  const [expenseMonth, setExpenseMonth] = useState(nowYM);

  const months6 = useMemo(() => lastNMonths(6), []);

  // ── data ──────────────────────────────────────────────────────────────────────
  const { data: allPayments = [], isLoading: pmtLoading, isError: pmtError, refetch: refetchPmt } = useQuery({
    queryKey: ['payments-admin'],
    queryFn: () => api.get('/api/admin/payments').then(r => r.data ?? []),
    staleTime: 2 * 60 * 1000,
  });

  const { data: expenses = [], isLoading: expLoading } = useQuery({
    queryKey: ['expenses', expenseMonth],
    queryFn: () =>
      api.get(`/api/expenses?billing_month=${expenseMonth}`).then(r => {
        const d = r.data;
        return Array.isArray(d) ? d : (d?.expenses ?? []);
      }),
    staleTime: 60 * 1000,
  });

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings-filter'],
    queryFn: () =>
      api.get('/api/properties/buildings').then(r => {
        const d = r.data;
        return Array.isArray(d) ? d : (d?.buildings ?? []);
      }),
    staleTime: 10 * 60 * 1000,
  });

  const deleteExpense = useMutation({
    mutationFn: (id) => api.delete(`/api/expenses/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses'] }),
    onError: (err) => toast.error(err?.response?.data?.detail || 'Xóa chi phí thất bại'),
  });

  const { data: expenseTemplates = [] } = useQuery({
    queryKey: ['expense-templates'],
    queryFn: () => api.get('/api/expense-templates').then(r => Array.isArray(r.data) ? r.data : []),
    staleTime: 5 * 60 * 1000,
  });

  const deleteTemplate = useMutation({
    mutationFn: (id) => api.delete(`/api/expense-templates/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expense-templates'] }),
  });

  const { data: expenseCategories = [] } = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get('/api/expenses/categories/standard').then(r => Array.isArray(r.data) ? r.data : []),
    staleTime: 5 * 60 * 1000,
  });
  // Helper: translate category key → label using API data (fallback to key)
  const catLabel = (key) => expenseCategories.find(c => c.key === key)?.label ?? key;

  const deleteCategory = useMutation({
    mutationFn: (key) => api.delete(`/api/expenses/categories/${key}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expense-categories'] }),
  });

  // ── current month stats ───────────────────────────────────────────────────────
  const currentYM = useMemo(nowYM, []);

  const currentMonthPayments = useMemo(
    () => allPayments.filter(p => (p.month ?? '').startsWith(currentYM)),
    [allPayments, currentYM]
  );

  const kpi = useMemo(() => {
    const total   = currentMonthPayments.reduce((s, p) => s + (p.final_amount ?? p.invoice_amount ?? 0), 0);
    const paid    = currentMonthPayments
      .filter(p => p.status === 'confirmed' || p.status === 'paid')
      .reduce((s, p) => s + (p.paid_amount ?? 0), 0);
    const pending = currentMonthPayments.filter(p => p.status === 'pending').length;
    const rate    = total > 0 ? Math.round((paid / total) * 100) : 0;
    return { total, paid, pending, rate };
  }, [currentMonthPayments]);

  // ── revenue trend chart data (6 months) ──────────────────────────────────────
  const trendData = useMemo(() => {
    const locale = intlLocale(lang);
    const labels = months6.map(m => {
      const [y, mo] = m.split('-');
      return new Date(Number(y), Number(mo) - 1).toLocaleDateString(locale, { month: 'short', year: '2-digit' });
    });
    const expected = months6.map(m =>
      allPayments
        .filter(p => (p.month ?? '').startsWith(m))
        .reduce((s, p) => s + (p.final_amount ?? p.invoice_amount ?? 0), 0)
    );
    const collected = months6.map(m =>
      allPayments
        .filter(p => (p.month ?? '').startsWith(m) && (p.status === 'confirmed' || p.status === 'paid'))
        .reduce((s, p) => s + (p.paid_amount ?? 0), 0)
    );
    return { labels, expected, collected };
  }, [allPayments, months6, lang]);

  const lineChartData = {
    labels: trendData.labels,
    datasets: [
      {
        label: t('finance:chart.expectedRevenue'),
        data: trendData.expected,
        borderColor: STONE,
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        borderDash: [4, 3],
        pointRadius: 3,
        pointBackgroundColor: STONE,
        tension: 0.3,
      },
      {
        label: t('finance:chart.collected'),
        data: trendData.collected,
        borderColor: TEAL,
        backgroundColor: `${TEAL_LIGHT}66`,
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: TEAL,
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const lineChartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: chartFont, boxWidth: 12, padding: 12 } },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${VND(ctx.parsed.y, lang)}`,
        },
      },
    },
    scales: {
      x: { ticks: { font: chartFont }, grid: { display: false } },
      y: {
        ticks: {
          font: chartFont,
          callback: (v) => v >= 1e6
            ? `${(v / 1e6).toFixed(0)}${t('finance:chart.axisMillionSuffix')}`
            : v.toLocaleString(intlLocale(lang)),
        },
        grid: { color: '#f5f5f4' },
      },
    },
  };

  // ── status breakdown donut ────────────────────────────────────────────────────
  const donutData = useMemo(() => {
    const confirmed = currentMonthPayments.filter(p => p.status === 'confirmed').length;
    const partial   = currentMonthPayments.filter(p => p.status === 'partial').length;
    const pending   = currentMonthPayments.filter(p => p.status === 'pending').length;
    const rejected  = currentMonthPayments.filter(p => p.status === 'rejected').length;
    return { confirmed, partial, pending, rejected };
  }, [currentMonthPayments]);

  const donutChartData = {
    labels: [
      t('finance:status.confirmed'),
      t('finance:status.partial'),
      t('finance:status.pending'),
      t('finance:status.rejected'),
    ],
    datasets: [{
      data: [donutData.confirmed, donutData.partial, donutData.pending, donutData.rejected],
      backgroundColor: ['#10b981', '#f59e0b', '#0d9488', '#ef4444'],
      borderWidth: 0,
      hoverOffset: 4,
    }],
  };

  const donutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    plugins: {
      legend: { position: 'bottom', labels: { font: chartFont, boxWidth: 10, padding: 10 } },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed}` } },
    },
  };

  // ── recent payments (last 8, current month) ───────────────────────────────────
  const recentPayments = useMemo(
    () => [...currentMonthPayments].slice(0, 8),
    [currentMonthPayments]
  );

  // ── expense totals ────────────────────────────────────────────────────────────
  const expenseTotal = useMemo(
    () => expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0),
    [expenses]
  );

  return (
    <div className="space-y-5">
      {pmtError && <QueryErrorBanner onRetry={refetchPmt} />}

      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">{t('finance:title')}</h1>
          <p className="text-sm text-stone-500 mt-0.5">{t('finance:subtitle')}</p>
        </div>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ['payments-admin'] })}
          className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors"
        >
          <Icons.Refresh size={14} />
          {t('common:action.refresh')}
        </button>
      </div>

      {/* ── Sub-tabs ── */}
      <div className="flex gap-1 border-b border-stone-200 -mb-2">
        {[
          { key: 'overview',   label: t('finance:tab.overview') },
          { key: 'report',     label: t('finance:tab.report') },
          { key: 'categories', label: t('finance:tab.categories') },
          { key: 'templates',  label: t('finance:tab.templates') },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-[13px] font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.key
                ? 'border-teal-600 text-teal-700'
                : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <>

      {/* ── KPI strip ── */}
      <KPIStrip>
        <KPI
          label={t('finance:kpi.revenue')}
          value={pmtLoading ? '…' : VND(kpi.total, lang)}
          icon={<Icons.Chart />}
          accent="primary"
        />
        <KPI
          label={t('finance:kpi.collected')}
          value={pmtLoading ? '…' : VND(kpi.paid, lang)}
          icon={<Icons.CheckCircle />}
          accent="success"
        />
        <KPI
          label={t('finance:kpi.pending')}
          value={pmtLoading ? '…' : String(kpi.pending)}
          sub={t('finance:kpi.pendingUnit')}
          icon={<Icons.Clock />}
          accent="warning"
        />
        <KPI
          label={t('finance:kpi.collectionRate')}
          value={pmtLoading ? '…' : `${kpi.rate}%`}
          icon={<Icons.Activity />}
          accent={kpi.rate >= 80 ? 'success' : kpi.rate >= 50 ? 'warning' : 'danger'}
        />
      </KPIStrip>

      {/* ── Charts row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue trend */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-stone-200 p-4">
          <p className="text-[13px] font-semibold text-stone-700 mb-3">{t('finance:chart.revenueTrend')}</p>
          <div style={{ height: 200 }}>
            <Line data={lineChartData} options={lineChartOpts} />
          </div>
        </div>

        {/* Status breakdown */}
        <div className="bg-white rounded-lg border border-stone-200 p-4">
          <p className="text-[13px] font-semibold text-stone-700 mb-3">{t('finance:chart.statusBreakdown')}</p>
          {currentMonthPayments.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-stone-400 text-sm">
              {t('common:status.noData')}
            </div>
          ) : (
            <div style={{ height: 200 }}>
              <Doughnut data={donutChartData} options={donutOpts} />
            </div>
          )}
        </div>
      </div>

      {/* ── Recent invoices ── */}
      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <p className="text-[13px] font-semibold text-stone-700">
            {t('finance:invoice.monthHeader', { month: currentYM })}
          </p>
          <a
            href="/manager/payment-management"
            className="text-[12px] font-medium text-teal-600 hover:text-teal-700 transition-colors"
          >
            {t('finance:invoice.viewAll')}
          </a>
        </div>
        {pmtLoading ? (
          <div className="divide-y divide-stone-100">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 h-11 px-4">
                <div className="h-3 bg-stone-100 rounded animate-pulse w-16" />
                <div className="h-3 bg-stone-100 rounded animate-pulse flex-1" />
                <div className="h-3 bg-stone-100 rounded animate-pulse w-24" />
              </div>
            ))}
          </div>
        ) : recentPayments.length === 0 ? (
          <EmptyState
            compact
            icon={<Icons.Receipt />}
            title={t('finance:invoice.empty.title')}
            description={t('finance:invoice.empty.description', { month: currentYM })}
          />
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100">
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">{t('finance:invoice.col.invoice')}</th>
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">{t('finance:invoice.col.renter')}</th>
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden sm:table-cell">{t('finance:invoice.col.room')}</th>
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">{t('finance:invoice.col.status')}</th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-stone-500">{t('finance:invoice.col.amount')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {recentPayments.map(p => (
                <tr key={p.id} className="hover:bg-stone-50 transition-colors h-10">
                  <td className="px-4 font-mono text-stone-600">#{String(p.id).padStart(4, '0')}</td>
                  <td className="px-4 text-stone-800 truncate max-w-[140px]">{p.renter_name || '—'}</td>
                  <td className="px-4 text-stone-500 hidden sm:table-cell">{p.room_number || '—'}</td>
                  <td className="px-4"><StatusPill status={p.status} /></td>
                  <td className="px-4 text-right font-medium text-stone-900 tabular-nums">
                    {VND(p.final_amount ?? p.invoice_amount, lang)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Expenses ── */}
      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-b border-stone-100">
          <div className="flex items-center gap-3 min-w-0">
            <p className="text-[13px] font-semibold text-stone-700 flex-shrink-0">{t('finance:expense.title')}</p>
            {expenseTotal > 0 && (
              <span className="text-[12px] font-medium text-stone-500 truncate">
                {t('finance:expense.totalLabel')} {VND(expenseTotal, lang)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <input
              type="month"
              value={expenseMonth}
              onChange={e => setExpenseMonth(e.target.value)}
              className="h-8 px-2.5 rounded-lg border border-stone-200 text-[12px] text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
            />
            <button
              onClick={() => setExpenseDrawer({ open: true, editing: null })}
              className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-teal-600 text-white text-[12px] font-medium hover:bg-teal-700 transition-colors"
            >
              <Icons.Plus size={13} />
              {t('finance:expense.addAction')}
            </button>
          </div>
        </div>

        {expLoading ? (
          <div className="divide-y divide-stone-100">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 h-11 px-4">
                <div className="h-3 bg-stone-100 rounded animate-pulse flex-1" />
                <div className="h-3 bg-stone-100 rounded animate-pulse w-20" />
              </div>
            ))}
          </div>
        ) : expenses.length === 0 ? (
          <EmptyState
            compact
            icon={<Icons.Receipt />}
            title={t('finance:expense.empty.title')}
            description={t('finance:expense.empty.description')}
            primaryAction={
              <button
                onClick={() => setExpenseDrawer({ open: true, editing: null })}
                className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors"
              >
                <Icons.Plus size={14} />
                {t('finance:expense.addAction')}
              </button>
            }
          />
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100">
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">{t('finance:expense.col.title')}</th>
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden sm:table-cell">{t('finance:expense.col.category')}</th>
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden md:table-cell">{t('finance:expense.col.date')}</th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-stone-500">{t('finance:expense.col.amount')}</th>
                <th className="w-16 px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {expenses.map(e => (
                <tr key={e.id} className="hover:bg-stone-50 transition-colors h-10">
                  <td className="px-4 text-stone-800 truncate max-w-[180px]">{e.title || '—'}</td>
                  <td className="px-4 text-stone-500 hidden sm:table-cell">{catLabel(e.category) || '—'}</td>
                  <td className="px-4 text-stone-500 hidden md:table-cell">
                    {e.expense_date ? new Date(e.expense_date).toLocaleDateString(intlLocale(lang)) : '—'}
                  </td>
                  <td className="px-4 text-right font-medium text-stone-900 tabular-nums">
                    {VND(e.amount, lang)}
                  </td>
                  <td className="px-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setExpenseDrawer({ open: true, editing: e })}
                        className="p-1.5 rounded-md text-stone-400 hover:bg-stone-100 transition-colors"
                        title={t('common:action.edit')}
                      >
                        <Icons.Edit size={13} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(t('finance:expense.confirmDelete'))) deleteExpense.mutate(e.id);
                        }}
                        className="p-1.5 rounded-md text-stone-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                        title={t('common:action.delete')}
                      >
                        <Icons.Trash size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Expense drawer ── */}
      <ExpenseDrawer
        open={expenseDrawer.open}
        onClose={() => setExpenseDrawer({ open: false, editing: null })}
        editing={expenseDrawer.editing}
        buildings={buildings}
      />

      </> /* end overview tab */}

      {/* ── Billing period report tab ── */}
      {activeTab === 'report' && (
        <FinanceBillingReport buildings={buildings} />
      )}

      {/* ── Categories tab ── */}
      {activeTab === 'categories' && <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <p className="text-[13px] font-semibold text-stone-700">{t('finance:expenseCategory.title')}</p>
          <button
            onClick={() => setCatDrawer({ open: true, editing: null })}
            className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-teal-600 text-white text-[12px] font-medium hover:bg-teal-700 transition-colors"
          >
            <Icons.Plus size={13} />
            {t('finance:expenseCategory.addAction')}
          </button>
        </div>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-100">
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">{t('finance:expenseCategory.col.label')}</th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden sm:table-cell">{t('finance:expenseCategory.col.key')}</th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden md:table-cell">{t('finance:expenseCategory.col.type')}</th>
              <th className="px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-stone-500">{t('finance:expenseCategory.col.source')}</th>
              <th className="w-16 px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {expenseCategories.map(cat => (
              <tr key={cat.key} className="hover:bg-stone-50 transition-colors h-10">
                <td className="px-4 text-stone-800 font-medium">{cat.label}</td>
                <td className="px-4 text-stone-400 font-mono text-[12px] hidden sm:table-cell">{cat.key}</td>
                <td className="px-4 text-stone-500 hidden md:table-cell">
                  {cat.type === 'operational' ? t('finance:expenseCategory.typeOperational') : t('finance:expenseCategory.typeOverhead')}
                </td>
                <td className="px-4 text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${cat.is_system ? 'text-blue-700 bg-blue-50 ring-blue-200' : 'text-teal-700 bg-teal-50 ring-teal-200'}`}>
                    {cat.is_system ? t('finance:expenseCategory.system') : t('finance:expenseCategory.custom')}
                  </span>
                </td>
                <td className="px-4">
                  {!cat.is_system && (
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setCatDrawer({ open: true, editing: cat })}
                        className="p-1.5 rounded-md text-stone-400 hover:bg-stone-100 transition-colors" title={t('common:action.edit')}>
                        <Icons.Edit size={13} />
                      </button>
                      <button onClick={() => { if (window.confirm(t('finance:expenseCategory.confirmDelete'))) deleteCategory.mutate(cat.key); }}
                        className="p-1.5 rounded-md text-stone-400 hover:bg-red-50 hover:text-red-500 transition-colors" title={t('common:action.delete')}>
                        <Icons.Trash size={13} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      {/* ── Templates tab ── */}
      {activeTab === 'templates' && <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <p className="text-[13px] font-semibold text-stone-700">{t('finance:expenseTemplate.title')}</p>
          <button
            onClick={() => setTmplDrawer({ open: true, editing: null })}
            className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-teal-600 text-white text-[12px] font-medium hover:bg-teal-700 transition-colors"
          >
            <Icons.Plus size={13} />
            {t('finance:expenseTemplate.addAction')}
          </button>
        </div>
        {expenseTemplates.length === 0 ? (
          <EmptyState compact icon={<Icons.FileCheck />} title={t('finance:expenseTemplate.empty')} />
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100">
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">{t('finance:expenseTemplate.col.name')}</th>
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden sm:table-cell">{t('finance:expenseTemplate.col.category')}</th>
                <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden md:table-cell">{t('finance:expenseTemplate.col.type')}</th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden md:table-cell">{t('finance:expenseTemplate.col.amount')}</th>
                <th className="px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden sm:table-cell">{t('finance:expenseTemplate.col.status')}</th>
                <th className="w-16 px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {expenseTemplates.map(tmpl => (
                <tr key={tmpl.id} className="hover:bg-stone-50 transition-colors h-10">
                  <td className="px-4 text-stone-800 truncate max-w-[160px]">{tmpl.name}</td>
                  <td className="px-4 text-stone-500 hidden sm:table-cell">{tmpl.category ? catLabel(tmpl.category) : '—'}</td>
                  <td className="px-4 text-stone-500 hidden md:table-cell">
                    {t(`finance:expenseTemplate.type.${tmpl.template_type}`, { defaultValue: tmpl.template_type })}
                  </td>
                  <td className="px-4 text-right font-medium text-stone-900 tabular-nums hidden md:table-cell">
                    {tmpl.template_type === 'fixed' && tmpl.base_amount != null ? VND(tmpl.base_amount, lang) :
                     tmpl.template_type === 'per_unit' && tmpl.unit_rate != null ? `${VND(tmpl.unit_rate, lang)}/${tmpl.unit_name || t('finance:expenseTemplate.unitFallback')}` :
                     tmpl.template_type === 'percentage' && tmpl.percentage_rate != null ? `${tmpl.percentage_rate}%` : '—'}
                  </td>
                  <td className="px-4 text-center hidden sm:table-cell">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${tmpl.is_active ? 'text-green-700 bg-green-50 ring-green-200' : 'text-stone-500 bg-stone-100 ring-stone-200'}`}>
                      {tmpl.is_active ? t('finance:expenseTemplate.active') : t('finance:expenseTemplate.inactive')}
                    </span>
                  </td>
                  <td className="px-4">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setTmplDrawer({ open: true, editing: tmpl })}
                        className="p-1.5 rounded-md text-stone-400 hover:bg-stone-100 transition-colors" title={t('common:action.edit')}>
                        <Icons.Edit size={13} />
                      </button>
                      <button onClick={() => { if (window.confirm(t('finance:expenseTemplate.confirmDelete'))) deleteTemplate.mutate(tmpl.id); }}
                        className="p-1.5 rounded-md text-stone-400 hover:bg-red-50 hover:text-red-500 transition-colors" title={t('common:action.delete')}>
                        <Icons.Trash size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>}

      {/* ── Expense category drawer ── */}
      <ExpenseCategoryDrawer
        open={catDrawer.open}
        onClose={() => setCatDrawer({ open: false, editing: null })}
        editing={catDrawer.editing}
      />

      {/* ── Expense template drawer ── */}
      <ExpenseTemplateDrawer
        open={tmplDrawer.open}
        onClose={() => setTmplDrawer({ open: false, editing: null })}
        editing={tmplDrawer.editing}
        buildings={buildings}
      />
    </div>
  );
}
